import { clsx } from 'clsx';
import { zodResolver } from '@hookform/resolvers/zod';
import { useEffect, useMemo } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { z } from 'zod';

import { Box } from '@repo/ui/Box';
import { Button } from '@repo/ui/Button';
import { Flex } from '@repo/ui/Flex';
import { Icon } from '@repo/ui/Icon';

import { AddMoreItemsDialog } from '../AddMoreItemsDialog/AddMoreItemsDialog';
import { Text } from '../Language/Language';
import { groupConsecutiveTasksByScope } from '../tasks/helpers';
import { taskComponentMap } from '../tasks/taskComponent';
import { Task } from '../tasks/Task/Task';
import type { OrderItem } from '../../graphql/query/getOrder';
import { useWorkflow } from '../../workflow/hooks';
import { filterItemsByEligibility } from '../../utils/item';
import { createSchemaFromTasks } from '../../workflow/schema';
import { type WorkflowData } from '../../workflow';
import { getItemsToBeAutoAddedForShipmentScope } from '../../utils/shipmentScopeUtils';
import { useShipmentMetadata } from '../../hooks/useShipmentGroups';
import { getBaseItemId } from '../../utils/itemKey';
import {
  processShipmentItems,
  processAutoAddedShipmentItems,
} from '../../utils/stepItemSelection';

import s from './Step.module.css';
import { NavigationFooter } from '../NavigationFooter/NavigationFooter';
import { TaskGrid } from '../TaskGrid/TaskGrid';
import { SubmitButton } from '../SubmitButton/SubmitButton';
import { ItemLoader } from './ItemLoader';
import { useRouter } from '@tanstack/react-router';

import { StepItem } from './StepItem';

export interface StepProps {
  formId: string;
  className?: string;
}

export const Step = (props: StepProps) => {
  const { formId, className } = props;
  const router = useRouter();
  const {
    canMoveToPrev,
    currentStep,
    config,
    currentStepIndex,
    data,
    isFirstStep,
    isLastStep,
    next,
    prev,
    goTo,
    order,
    updateData,
    isLoading: workflowIsLoading,
    isSaving,
  } = useWorkflow();
  const { shipmentGroups, hasMultipleShipments } = useShipmentMetadata();

  const { items } = order ?? { items: [] };
  const selectedItemIds = Object.keys(data?.items || {});
  const { tasks } = currentStep ?? { tasks: [] };
  const tasksGroupedByScope = groupConsecutiveTasksByScope(tasks);

  const StepSchema = createSchemaFromTasks(tasks, data);
  type StepSchemaType = z.infer<typeof StepSchema>;

  const methods = useForm<StepSchemaType>({
    resolver: zodResolver(StepSchema),
    defaultValues: data as StepSchemaType,
    mode: 'onChange',
    reValidateMode: 'onChange',
  });
  const { watch, getValues, handleSubmit, formState } = methods;

  const { isLoading, isValid } = formState;
  const doesButtonAppearDisabled = isLoading || !isValid || isSaving;

  const eligibleItems = useMemo(
    () => filterItemsByEligibility(items as OrderItem[], true) ?? [],
    [items],
  );

  const itemsById = useMemo(() => {
    const itemMap: Record<string, OrderItem> = {};

    items.forEach((item) => {
      // Use the actual item.id as the key
      itemMap[item.id] = item;
    });

    return itemMap;
  }, [items]);

  // Get items that would be automatically added due to shipment scope reasons
  const itemsToBeAutoAdded = useMemo(
    () => getItemsToBeAutoAddedForShipmentScope(data, order),
    [data, order],
  );

  const itemsAvailableForSelection = useMemo(() => {
    // For multiple shipments, create shipment-specific items
    if (hasMultipleShipments) {
      return processShipmentItems(
        shipmentGroups,
        eligibleItems,
        data || undefined,
        order,
      );
    }

    // For single shipment, use the original logic
    return eligibleItems.filter((item) => {
      // Exclude items already in cart
      if (data?.items?.[item.id]) {
        return false;
      }
      // Exclude items that would be auto-added due to shipment scope
      if (itemsToBeAutoAdded.includes(item.id)) {
        return false;
      }
      return true;
    });
  }, [
    eligibleItems,
    data,
    itemsToBeAutoAdded,
    hasMultipleShipments,
    shipmentGroups,
    order,
  ]);

  // Separate items that would be auto-added (to show as disabled)
  const itemsToBeAutoAddedObjects = useMemo(() => {
    // For multiple shipments, create shipment-specific auto-added items
    if (hasMultipleShipments) {
      return processAutoAddedShipmentItems(
        shipmentGroups,
        eligibleItems,
        itemsToBeAutoAdded,
        data || undefined,
        order,
      );
    }

    // For single shipment, use the original logic
    return eligibleItems.filter(
      (item) => itemsToBeAutoAdded.includes(item.id) && !data?.items?.[item.id],
    );
  }, [
    eligibleItems,
    itemsToBeAutoAdded,
    data,
    hasMultipleShipments,
    shipmentGroups,
    order,
  ]);
  const addMoreItemsVisible = !isFirstStep && !isLastStep;
  const isAddMoreItemsDisabled =
    addMoreItemsVisible &&
    itemsAvailableForSelection.length > 0 &&
    !doesButtonAppearDisabled
      ? false
      : true;

  useEffect(() => {
    // Handles step ID synchronization for browser based back/forward navigation actions
    const unsub = router.subscribe('onResolved', (event) => {
      const currentRoute = event.toLocation.pathname;
      if (!config) return;
      const prevStep = config.steps[currentStepIndex - 1];
      const nextStep = config.steps[currentStepIndex + 1];
      if (prevStep && prevStep.route === currentRoute) {
        goTo(prevStep.id);
      }
      if (nextStep && nextStep.route === currentRoute) {
        goTo(nextStep.id);
      }
    });
    return unsub;
  }, [router, config, currentStepIndex, goTo]);

  useEffect(() => {
    if (!tasks) {
      return;
    }

    const { unsubscribe } = watch((data) => {
      updateData(data as WorkflowData);
    });

    return () => unsubscribe();
  }, [tasks, watch, updateData]);

  const handleNext = handleSubmit((data, e) => {
    e?.preventDefault();
    next(data as WorkflowData);
  });

  const handlePrev = () => {
    updateData(getValues() as WorkflowData);
    prev();
  };

  return (
    <FormProvider {...methods}>
      <Box isContainer>
        <form
          className={s.form}
          id={formId}
          autoComplete="off"
          onSubmit={handleNext}
        >
          <Flex direction="column" gap={['g-24']}>
            <Box className={className}>
              {tasksGroupedByScope.map((group) => {
                const { scope, mode } = group;
                const showItemCard =
                  scope === 'item' &&
                  mode !== 'implicit' &&
                  currentStep?.id !== 'itemSelection';
                return (
                  <Flex
                    alignItems="flex-end"
                    as="section"
                    className={clsx(s[`${scope}Group`], s[`${mode}Mode`])}
                    direction="column"
                    gap={['g-16']}
                    key={group.id}
                  >
                    {showItemCard ? (
                      selectedItemIds?.map((itemId) => {
                        // Use itemKey utility to safely extract the base item ID
                        const actualItemId = getBaseItemId(itemId);
                        let orderItem: OrderItem | undefined =
                          itemsById[actualItemId];

                        // If we can't find the item with actualItemId, try the full itemId
                        if (!orderItem) {
                          orderItem = itemsById[itemId];
                        }

                        return (
                          <Flex direction="column" gap="g-48" key={itemId}>
                            {workflowIsLoading.order ? (
                              <ItemLoader />
                            ) : (
                              <StepItem
                                orderItem={orderItem}
                                itemId={itemId}
                                group={group}
                              />
                            )}
                          </Flex>
                        );
                      })
                    ) : (
                      <Flex
                        className={s.cartTasks}
                        direction="column"
                        gap="g-16"
                        justifyContent="flex-end"
                      >
                        <TaskGrid step={currentStep?.id}>
                          {group.tasks.map((task) => {
                            const { id } = task;
                            const TaskComponent = taskComponentMap[id];

                            return (
                              <Task key={id} taskId={id}>
                                <TaskComponent handleNext={handleNext} />
                              </Task>
                            );
                          })}
                        </TaskGrid>
                      </Flex>
                    )}
                  </Flex>
                );
              })}
            </Box>
            {currentStep?.id === 'reasons' && addMoreItemsVisible && (
              <AddMoreItemsDialog
                isDisabled={isAddMoreItemsDisabled}
                items={itemsAvailableForSelection}
                disabledItems={itemsToBeAutoAddedObjects}
              />
            )}

            {currentStep?.id !== 'itemSelection' && (
              <NavigationFooter
                // TODO: Get this moved to a retailer level config
                isFixed={currentStep?.id !== 'returnMethod'}
                leftChildren={
                  canMoveToPrev && (
                    <Button
                      onClick={handlePrev}
                      size="lg"
                      variant="secondary"
                      isFullWidth
                    >
                      <Icon name="arrow-left" />
                      <Text langKey="Global.back" />
                    </Button>
                  )
                }
                rightChildren={
                  isLastStep ? (
                    <SubmitButton isFullWidth={true} />
                  ) : (
                    <Button
                      ariaDisabled={doesButtonAppearDisabled}
                      isFullWidth
                      size="lg"
                      type="submit"
                      variant="primary"
                      className={s.nextButton}
                      disabled={doesButtonAppearDisabled}
                    >
                      <Text langKey="Global.next" />
                    </Button>
                  )
                }
              />
            )}
          </Flex>
        </form>
      </Box>
    </FormProvider>
  );
};
